import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import PublicShell from '../components/PublicShell'
import Spinner from '../components/Spinner'
import CallRecordPanel from '../components/signals/CallRecordPanel'
import ShareMenu from '../components/signals/ShareMenu'
import SignalDetailCard from '../components/signals/SignalDetailCard'
import { creatorRecordPath, recordPath } from '../routes'
import useLivePrice from '../hooks/useLivePrice'
import { publicCallsAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { PublicCall } from '../types'
import { isOpenSignal } from '../components/signals/signalChips'
import { callerHandle, isCreatorCall } from '../utils/callShareText'
import { formatUtcSeconds } from '../utils/utcTime'

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'error' }
  | { kind: 'ready'; call: PublicCall }

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status

export const VERIFIED_LINE =
  'Verified means this call was locked before its outcome and is scored by fixed public rules. It does not mean the call is good.'

/** Who made a creator's call, their note, and what "verified" does and doesn't mean. */
const CreatorPanel = ({ call }: { call: PublicCall }) => (
  <section
    className="mb-4 rounded-xl border border-terminal-border bg-terminal-surface p-4 text-sm"
    data-testid="call-creator"
  >
    <p className="text-gray-300">
      Called by{' '}
      <span className="font-semibold text-gray-100">
        {call.creator?.deleted
          ? 'a deleted account'
          : callerHandle(call)
            ? `@${callerHandle(call)}`
            : 'a FinCreator creator'}
      </span>
    </p>
    {call.creator?.note && (
      <blockquote className="mt-2 border-l-2 border-terminal-border pl-3 italic text-gray-300" data-testid="call-note">
        {call.creator.note}
      </blockquote>
    )}
    {call.creator?.current_handle && (
      <p className="mt-2">
        <Link
          to={creatorRecordPath(call.creator.current_handle)}
          className="text-accent hover:underline"
          data-testid="creator-record-link"
        >
          See every call by @{call.creator.current_handle} →
        </Link>
      </p>
    )}
    {call.creator?.deleted && call.creator.handle && (
      // A deleted account's record is still found by the handle it locked under.
      <p className="mt-2">
        <Link
          to={creatorRecordPath(call.creator.handle)}
          className="text-accent hover:underline"
          data-testid="creator-record-link"
        >
          See every call by this deleted account →
        </Link>
      </p>
    )}
    <p className="mt-3 text-xs text-gray-400" data-testid="verified-line">
      {VERIFIED_LINE}
    </p>
  </section>
)

/**
 * /r/<id>: one call, public, no login. The signal card as signed-in readers
 * see it on Signals (an open call's live price from the public ticker, no
 * trade button), then the call's record. Open, won, lost and excluded calls share one layout.
 */
const PublicSignal = () => {
  const { id = '' } = useParams<{ id: string }>()
  const signedIn = useAuthStore((st) => Boolean(st.token))
  const userId = useAuthStore((st) => (st.token ? st.user?.id : undefined))
  const justLocked = Boolean((useLocation().state as { justLocked?: boolean } | null)?.justLocked)
  const [state, setState] = useState<State>({ kind: 'loading' })
  const isOwner = state.kind === 'ready' && userId != null && state.call.creator?.id === userId
  const openSignal = state.kind === 'ready' && state.call.signal && isOpenSignal(state.call.signal) ? state.call.signal : null
  // An open call's live price, re-read every ~30s; the Current box is left
  // out until one comes back.
  const price = useLivePrice(openSignal?.symbol, openSignal != null)

  useEffect(() => {
    let cancelled = false
    if (!/^\d+$/.test(id) || Number(id) < 1) {
      setState({ kind: 'missing' })
      return undefined
    }
    setState({ kind: 'loading' })
    publicCallsAPI
      .get(id)
      .then((call) => !cancelled && setState({ kind: 'ready', call }))
      .catch((error) => {
        if (cancelled) return
        setState({ kind: statusOf(error) === 404 ? 'missing' : 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <PublicShell>
      {state.kind === 'loading' && (
        <div className="flex justify-center py-16" data-testid="call-loading">
          <Spinner />
        </div>
      )}
      {state.kind === 'missing' && (
        <div className="py-16 text-center" data-testid="call-not-found">
          <h1 className="text-xl font-semibold text-gray-100">No call with that id</h1>
          <p className="mt-2 text-sm text-gray-400">
            Check the link. Only published calls have a public page.
          </p>
        </div>
      )}
      {state.kind === 'error' && (
        <div className="py-16 text-center" data-testid="call-error">
          <h1 className="text-xl font-semibold text-gray-100">This call could not be loaded</h1>
          <p className="mt-2 text-sm text-gray-400">Try again in a minute.</p>
        </div>
      )}
      {state.kind === 'ready' && (
        <>
          <p className="mb-3 text-xs uppercase tracking-wider text-gray-500" data-testid="call-heading">
            Call #{state.call.id}
          </p>
          {isCreatorCall(state.call) && <CreatorPanel call={state.call} />}
          <ShareMenu call={state.call} isOwner={isOwner} justLocked={justLocked} />
          {state.call.signal ? (
            <SignalDetailCard
              signal={state.call.signal}
              price={price}
              now={Date.now()}
              hideMissingPrice
              showRecordedSymbol
              creatorCall={isCreatorCall(state.call)}
            />
          ) : (
            <div
              className="rounded-xl border border-terminal-border bg-terminal-surface p-6 text-sm text-gray-300"
              data-testid="call-sealed"
            >
              <p className="font-semibold text-gray-100">A premium call, still open.</p>
              <p className="mt-2">
                It was published {formatUtcSeconds(state.call.published_at)}. Its levels are for subscribers until it
                closes; then this page shows it in full.
              </p>
            </div>
          )}
          <CallRecordPanel call={state.call} />
          <p className="mt-4 text-sm text-gray-400">
            <Link to={recordPath(signedIn)} className="text-accent hover:underline" data-testid="whole-record-link">
              {isCreatorCall(state.call)
                ? "See the FinCreator engine's track record (creator calls don't count in it) →"
                : 'See the whole track record →'}
            </Link>
          </p>
        </>
      )}
    </PublicShell>
  )
}

export default PublicSignal
